<template>
  <!-- 外层容器：fullScreen=true 全屏固定；fullScreen=false 内嵌 -->
  <div class="wrap" :class="props.fullScreen ? 'fullscreen-mode' : 'inline-mode'">
    <!-- 视口遮罩：宽度40vw，居中，点击左右半区切换 -->
    <div class="slider-viewport" @click="handleViewportClick">
      <!-- 所有卡片用绝对定位，由 JS 控制位置和层级 -->
      <div
        v-for="(item, index) in imgList"
        :key="index"
        class="ratio-container"
        :style="getCardStyle(index)"
      >
        <img class="slide-img" :src="item.src" alt="" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import img1 from '@/assets/Picture/1783251336265.png'
import img2 from '@/assets/Picture/1783731556342.png'
import img3 from '@/assets/Picture/1784961541603.png'
import img4 from '@/assets/Picture/1785160984588.png'
import img5 from '@/assets/Picture/1786873613762.png'
import img6 from '@/assets/Picture/1787235606604.png'
import img7 from '@/assets/Picture/1789428972525.png'

const props = defineProps({
  fullScreen: {
    type: Boolean,
    default: false
  }
})

// 轮播图片数组
const imgList = ref([
  { src: img1 },
  { src: img2 },
  { src: img3 },
  { src: img4 },
  { src: img5 },
  { src: img6 },
  { src: img7 }
])

// 当前居中卡片索引
const currentIndex = ref(0)

// 响应式尺寸
const windowWidth = ref(window.innerWidth)
const windowHeight = ref(window.innerHeight)

const updateSize = () => {
  windowWidth.value = window.innerWidth
  windowHeight.value = window.innerHeight
}

let autoTimer

// 重启自动轮播定时器（手动切换后重置时间）
const restartAutoPlay = () => {
  if (autoTimer) clearInterval(autoTimer)
  autoTimer = setInterval(() => {
    next()
  }, 5000)
}

onMounted(() => {
  window.addEventListener('resize', updateSize)
  // 自动轮播，每5秒向左滑动
  restartAutoPlay()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateSize)
  clearInterval(autoTimer)
})

// slider-viewport 尺寸：宽度 40vw，高度按比例
const viewportWidth = computed(() => windowWidth.value * 0.9)
const viewportHeight = computed(() => windowHeight.value * 0.9)

// 卡片尺寸：在 viewport 内保持 9:16，高度撑满 viewport
const cardHeight = computed(() => viewportHeight.value)
const cardWidth = computed(() => (cardHeight.value * 9) / 16)

// 每张卡片露出的宽度
const gap = 40

// 可见范围：只渲染 diff 在 [-range, +range] 内的卡片，优化性能
const visibleRange = 3

// 根据卡片索引计算样式（位置、层级、缩放）
const getCardStyle = (index) => {
  // 计算差值（考虑循环）
  const total = imgList.value.length
  let diff = index - (currentIndex.value % total)
  // 将 diff 归约到 [-floor(total/2), floor(total/2)] 范围，实现循环
  if (diff > Math.floor(total / 2)) diff -= total
  if (diff < -Math.floor(total / 2)) diff += total

  const absDiff = Math.abs(diff)

  // 超出可见范围：隐藏
  if (absDiff > visibleRange) {
    return {
      position: 'absolute',
      opacity: 0,
      pointerEvents: 'none',
      transition: 'all 0.4s ease-out'
    }
  }

  // z-index：越靠近中间越高
  const zIndex = 100 - absDiff

  // 缩放：中间 1，两侧逐渐缩小
  const scale = 1 - absDiff * 0.05

  // 透明度：中间 1，两侧逐渐半透明
  const opacity = 1 - absDiff * 0.15

  // 计算水平位置（相对于 slider-viewport）
  // 核心思路：左右对称，每张卡片露出 gap 宽度
  // diff=0 居中；diff=+1 左边缘在 50%+gap；diff=-1 右边缘在 50%-gap
  let left

  if (diff === 0) {
    // 当前卡片：在 viewport 中居中
    left = `calc(50% - ${cardWidth.value / 2}px)`
  } else if (diff > 0) {
    // 右侧卡片：左边缘 = 50% + diff * gap
    left = `calc(50% + ${diff * gap}px)`
  } else {
    // 左侧卡片：右边缘 = 50% + diff * gap（diff为负数），所以 left = 50% + diff*gap - 卡片宽度
    left = `calc(50% + ${diff * gap}px - ${cardWidth.value}px)`
  }

  return {
    position: 'absolute',
    left,
    top: `calc(50% - ${cardHeight.value / 2}px)`,
    width: `${cardWidth.value}px`,
    height: `${cardHeight.value}px`,
    transform: `scale(${scale})`,
    zIndex,
    opacity,
    transition: 'all 0.4s ease-out'
  }
}

// 切换逻辑（无限循环）
const next = () => {
  currentIndex.value++
}
const prev = () => {
  currentIndex.value--
}

// 点击 viewport 左右半区切换
const handleViewportClick = (e) => {
  const rect = e.currentTarget.getBoundingClientRect()
  const clickX = e.clientX - rect.left
  const midX = rect.width / 2

  if (clickX > midX) {
    next()
  } else {
    prev()
  }

  // 手动切换后重置自动轮播计时，避免快速自动切换
  restartAutoPlay()
}
</script>

<style scoped>
/* 内嵌模式（默认）：随父容器定位，透明背景 */
.wrap.inline-mode {
  position: relative;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: 1;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

/* 全屏模式：固定覆盖整个视口 */
.wrap.fullscreen-mode {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #000;
  z-index: 999;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

/* 轮播可视窗口：宽度40vw，居中 */
.slider-viewport {
  position: relative;
  width: 90vw;
  height: 90vh;
  overflow: hidden;
}

/* 单个轮播卡片 */
.ratio-container {
  flex-shrink: 0;
  aspect-ratio: 9 / 16;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.4), 0 0 30px rgba(255, 255, 255, 0.2);
}

/* 图片填满卡片 */
.slide-img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
}

</style>
